import {
    importFileToSheets,
    toCelldataSheet,
    isSheetS3StorageEnabled,
    uploadSheetVersion,
    countFilledCells,
    isReadOnlySize,
    SheetReadOnlyAddError,
} from '../SheetEditor';
import type { FortuneSheet, ReadOnlyAddBand } from '../SheetEditor';
import type { EditedWorkspaceSnapshot } from './editedWorkspaceSnapshot';
import { isXlsxBuffer } from '../DocumentViewer/spreadsheetBytes';
import { docxBlobToPlateValue } from '../PlateEditor/serializers/docxBlobToPlateValue';
import { txtBlobToPlateValue } from '../PlateEditor/serializers/txtBlobToPlateValue';
import { processImagesBeforeSave } from '../PlateEditor/services/imageUploadService';
import { buildPlateSaveFields } from '../PlateEditor/services/plateStorage';
import { saveDocument } from '../../store/investor/investorApiHelper';
import { CUSTOM_DOC_TYPE, REPORT_EDITOR } from '../../modules/Reports/constants';
import { STORAGE_REF_TYPE, uploadBlobVersion } from '../storage/mnemosyneBlobStorage';

const SHEET_MIMES = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/csv',
    'application/csv',
];
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const TXT_MIME = 'text/plain';
const PDF_MIME = 'application/pdf';

type AddKind = 'sheet' | 'docx' | 'txt' | 'pdf';

/** The Add-to-Reports fallback toast, shared by every surface that opens a
 *  file into Reports (FileViewerDrawer, Magic Upload, Ask AICA, DataRoom). */
export const ADD_TO_REPORTS_FAILED_MESSAGE = 'Failed to add this file to Reports. Please try again.';

/**
 * Classify a file on a VALIDATED signal for Add-to-Reports: extension first,
 * else the passed mime (Ask AICA files can be extension-less — `fileType` is
 * the authoritative signal there). Anything outside {xlsx,xls,csv,docx,txt,pdf} —
 * legacy .doc, unknown — is NOT addable and must never reach mammoth or save an
 * empty CUSTOM doc. `pdf` is view-only: stored verbatim, never parsed.
 */
export function classifyAddable(fileName: string, fileType?: string): AddKind | null {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    if (['xlsx', 'xls', 'csv'].includes(ext)) return 'sheet';
    if (ext === 'docx') return 'docx';
    if (ext === 'txt') return 'txt';
    if (ext === 'pdf') return 'pdf';

    const mime = (fileType ?? '').split(';')[0].trim().toLowerCase();
    if (SHEET_MIMES.includes(mime)) return 'sheet';
    if (mime === DOCX_MIME) return 'docx';
    if (mime === TXT_MIME) return 'txt';
    if (mime === PDF_MIME) return 'pdf';
    return null;
}

const asFile = (file: File | Blob, fileName: string, fileType?: string): File =>
    file instanceof File ? file : new File([file], fileName, fileType ? { type: fileType } : undefined);

export type { EditedWorkspaceSnapshot } from './editedWorkspaceSnapshot';
export { isEditedWorkspaceSnapshot } from './editedWorkspaceSnapshot';

/** Everything a view drawer's Add to Workspace can hand its host: a
 *  size-capped workbook's original bytes (`ReadOnlyAddBand`, see
 *  `isReadOnlyAddBand`), the edited on-screen content, or `undefined`
 *  for an untouched file. A size-capped workbook can't be edited, so the
 *  first two never compete. */
export type AddToWorkspacePayload = ReadOnlyAddBand | EditedWorkspaceSnapshot;

type ReportBaseArgs = Pick<AddFileToReportsArgs, 'fileName' | 'assesseeOrgId' | 'investorOrgId' | 'createdBy'>;

/** The CUSTOM-document fields every Add to Workspace save shares. */
const reportBase = ({ fileName, assesseeOrgId, investorOrgId, createdBy }: ReportBaseArgs) => ({
    assesseeOrgId,
    ...(investorOrgId ? { investorOrgId } : {}),
    ...(createdBy ? { createdBy } : {}),
    documentType: CUSTOM_DOC_TYPE,
    name: fileName.replace(/\.(docx|xlsx?|csv|txt|pdf)$/i, '') || fileName,
    description: '',
});

/**
 * Persist sheets as a `editor:'sheet'` Workspace doc. `runtimeSheets` says the
 * sheets are the live workbook's 2D `data` shape (an edited snapshot) rather
 * than already-celldata (a freshly parsed import) — the S3 path lets the
 * worker do that conversion in the same round trip as the xlsx build, the
 * inline kill-switch path converts on the main thread (mirrors
 * `useSheetEditorDrawerLogic.persistSheet`).
 *
 * S3-backed snapshot when enabled — mirrors useSheetEditorDrawerLogic.handleSave:
 * upload the xlsx to S3 and persist a small storage ref, so a large workbook
 * added via "Add to Reports" (Data Room / Magic Upload / Ask AICA / preview
 * drawer) doesn't hit Mongo's 16 MB cap. No inline fallback on failure: a
 * workbook large enough to need S3 would fail the same way against Mongo's
 * cap, just with a more confusing error — the caller already toasts on throw.
 */
const saveSheetDoc = async (ctx: ReportBaseArgs, sheets: FortuneSheet[], runtimeSheets: boolean): Promise<any> => {
    const base = reportBase(ctx);
    if (isSheetS3StorageEnabled()) {
        const ref = await uploadSheetVersion({
            sheets,
            assesseeOrgId: ctx.assesseeOrgId,
            investorOrgId: ctx.investorOrgId,
            ...(runtimeSheets ? { runtimeSheets: true } : {}),
        });
        return saveDocument({ ...base, editor: 'sheet', formatType: 'XLSX', storage: ref });
    }
    return saveDocument({
        ...base,
        editor: 'sheet',
        formatType: 'XLSX',
        content: { version: 1, editor: 'sheet', value: runtimeSheets ? sheets.map(toCelldataSheet) : sheets },
    });
};

/**
 * Persist a Plate value as an `editor:'plate'` Workspace doc — the exact shape
 * `ReportEditorRoute`'s persist uses. Offloads images first so a
 * screenshot-heavy DOCX doesn't blow Mongo's 10 MB cap on the snapshot save,
 * then the same hybrid as the editor persist: inline if it still fits, S3 otherwise.
 */
const savePlateDoc = async (ctx: ReportBaseArgs, value: any[]): Promise<any> => {
    const base = reportBase(ctx);
    const processed = await processImagesBeforeSave(value, [], REPORT_EDITOR.UPLOAD_SOURCE);
    const body = await buildPlateSaveFields({
        value: processed,
        version: 1,
        assesseeOrgId: ctx.assesseeOrgId,
        investorOrgId: ctx.investorOrgId,
        isDraft: false,
    });
    return saveDocument({
        ...base,
        editor: 'plate',
        formatType: 'DOC',
        ...(body.content ? { content: body.content } : { content: null }),
        storage: body.storage ?? null,
    });
};

export interface AddEditedFileToReportsArgs extends ReportBaseArgs {
    snapshot: EditedWorkspaceSnapshot;
}

/**
 * Snapshot the EDITED on-screen content of a file into Workspace — what the
 * editable drawers hand back when the user changed a spreadsheet or document
 * before clicking "Add to Workspace". Saves the exact same document shapes as
 * `addFileToReports`, just sourced from the live editor instead of re-parsing
 * the original bytes. The original upload is never touched.
 */
export async function addEditedFileToReports({ snapshot, ...ctx }: AddEditedFileToReportsArgs): Promise<any> {
    if (snapshot.kind === 'sheet') {
        if (!snapshot.sheets.length) throw new Error('Cannot add an empty workbook to Workspace');
        return saveSheetDoc(ctx, snapshot.sheets, true);
    }
    return savePlateDoc(ctx, snapshot.value);
}

export interface AddFileToReportsArgs {
    file: File | Blob;
    fileName: string;
    fileType?: string;
    assesseeOrgId: string;
    investorOrgId?: string;
    createdBy?: string;
}

/**
 * Snapshot a file into the Reports module as a `documentType:CUSTOM` doc — the
 * shared entry point for Ask AICA / Data Room "Add to Reports" and the XLSX
 * drawer's primary button. Routes on `classifyAddable`; xlsx/xls/csv save the
 * exact shape `useSheetEditorDrawerLogic.handleSave` uses, docx/txt the exact
 * shape `ReportEditorRoute`'s persist uses (txt reuses the docx payload shape,
 * only the conversion step differs) — this is a direct snapshot (convert +
 * save), NOT `useReportsLogic.handleImportFile` (which only converts and opens
 * a drawer for the user to save later).
 */
export async function addFileToReports({
    file,
    fileName,
    fileType,
    assesseeOrgId,
    investorOrgId,
    createdBy,
}: AddFileToReportsArgs): Promise<any> {
    const kind = classifyAddable(fileName, fileType);
    if (!kind) {
        // Deterministic, handled rejection — never reaches mammoth, never saves
        // an empty doc. Caller toasts.
        throw new Error(`Add to Reports supports .docx, .xlsx/.xls/.csv, .txt and .pdf only, not "${fileName}"`);
    }

    const ctx: ReportBaseArgs = { fileName, assesseeOrgId, investorOrgId, createdBy };

    if (kind === 'pdf') {
        // Never editable, so no parse step and no inline fallback — the bytes are the document.
        const docId = await uploadBlobVersion({
            bytes: await file.arrayBuffer(),
            mime: PDF_MIME,
            extension: 'pdf',
            fileNamePrefix: 'report',
            assesseeOrgId,
            investorOrgId,
            // Each add mints its own doc and key, so the snapshot is immutable and
            // there is no lineage to version.
            skipVersionRegister: true,
        });
        return saveDocument({ ...reportBase(ctx), editor: 'pdf', formatType: 'PDF', storage: { type: STORAGE_REF_TYPE, docId } });
    }

    if (kind === 'sheet') {
        // Every import error, including SheetTooLargeError, propagates
        // unchanged — rewriting it here would erase the type callers need to
        // detect the too-large case and show the friendly copy themselves
        // (sheetLoadErrorMessage).
        const parsed = await importFileToSheets(asFile(file, fileName, fileType));
        const filledCells = countFilledCells(parsed);
        if (isReadOnlySize(filledCells)) {
            // Too big to re-serialise (exceljs would cross the tab's memory
            // budget): store the original .xlsx bytes as-is; csv/xls can't be.
            return addOriginalXlsxToReports({
                rawBytes: await file.arrayBuffer(),
                filledCells,
                fileName,
                assesseeOrgId,
                investorOrgId,
                createdBy,
            });
        }
        return saveSheetDoc(ctx, parsed.map(toCelldataSheet), false);
    }

    const value = kind === 'txt' ? await txtBlobToPlateValue(file) : await docxBlobToPlateValue(file);
    return savePlateDoc(ctx, value);
}

/**
 * Snapshot a workbook over the editable cap by storing its ORIGINAL .xlsx bytes
 * as-is (re-serialising it with exceljs would cross the tab's memory budget).
 * Takes the bytes and filled-cell count the caller already holds, so nothing is
 * fetched or parsed again.
 */
export async function addOriginalXlsxToReports({ rawBytes, filledCells, ...reportArgs }: ReadOnlyAddBand & ReportBaseArgs): Promise<any> {
    if (!isSheetS3StorageEnabled() || !isXlsxBuffer(rawBytes)) throw new SheetReadOnlyAddError(filledCells);
    const ref = await uploadSheetVersion({ rawBytes, assesseeOrgId: reportArgs.assesseeOrgId, investorOrgId: reportArgs.investorOrgId });
    return saveDocument({ ...reportBase(reportArgs), editor: 'sheet', formatType: 'XLSX', storage: ref });
}

export interface AddFileUrlToReportsArgs extends Omit<AddFileToReportsArgs, 'file'> {
    fileUrl: string;
}

/**
 * Same as `addFileToReports`, but for callers that only hold a fetchable URL
 * (presigned S3 link or a local `blob:` URL) rather than an already-resolved
 * File/Blob — e.g. FileViewerDrawer's Add-to-Reports action on a preview
 * drawer, which never downloads the file up front.
 */
export async function addFileUrlToReports({
    fileUrl,
    fileName,
    fileType,
    assesseeOrgId,
    investorOrgId,
    createdBy,
}: AddFileUrlToReportsArgs): Promise<any> {
    if (!classifyAddable(fileName, fileType)) {
        throw new Error(`Add to Reports supports .docx, .xlsx/.xls/.csv, .txt and .pdf only, not "${fileName}"`);
    }
    const response = await fetch(fileUrl);
    if (!response.ok) throw new Error('Failed to fetch file');
    const blob = await response.blob();
    return addFileToReports({ file: blob, fileName, fileType, assesseeOrgId, investorOrgId, createdBy });
}
